import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Shield, Plus, X, FileText, Image, Camera, Upload, Calendar, AlertTriangle, Edit, Check } from 'lucide-react';

interface DocumentsSectionProps {
  data: Record<string, any>;
  onChange: (data: Record<string, any>) => void;
}

export const DocumentsSection = ({ data, onChange }: DocumentsSectionProps) => {
  const [editingDocument, setEditingDocument] = useState<number | null>(null);

  const handleChange = (field: string, value: any) => {
    onChange({
      ...data,
      [field]: value
    });
  };

  const addDocument = () => {
    const files = data.files || [];
    const newIndex = files.length;
    files.push({
      file_id: '',
      type: 'certificate',
      title: '',
      description: '',
      upload_date: new Date().toISOString().split('T')[0],
      expiry_date: '',
      issue_date: '',
      file_url: '',
      section_ref: '',
      reminder_days: 30,
      issuing_authority: '',
      document_number: ''
    });
    handleChange('files', files);
    setEditingDocument(newIndex);
  };

  const updateDocument = (index: number, field: string, value: string | number) => {
    const files = [...(data.files || [])];
    files[index] = { ...files[index], [field]: value };
    handleChange('files', files);
  };

  const removeDocument = (index: number) => {
    const files = data.files || [];
    files.splice(index, 1);
    handleChange('files', files);
    setEditingDocument(null);
  };

  const saveDocument = (index: number) => {
    setEditingDocument(null);
  };

  // Helper functions for document status
  const isExpiringSoon = (expiryDate: string, reminderDays: number = 30) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    const today = new Date();
    const daysUntilExpiry = Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= reminderDays && daysUntilExpiry > 0;
  };

  const isExpired = (expiryDate: string) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    const today = new Date();
    return expiry < today;
  };

  const documentTypes = [
    { value: 'certificate', label: 'Certificato', icon: '📜' },
    { value: 'passport', label: 'Passaporto EU', icon: '📘' },
    { value: 'insurance', label: 'Assicurazione', icon: '🛡️' },
    { value: 'license', label: 'Licenza', icon: '📜' },
    { value: 'registration', label: 'Registrazione', icon: '📋' },
    { value: 'health_card', label: 'Libretto Sanitario', icon: '💊' },
    { value: 'photo', label: 'Foto', icon: '📸' },
    { value: 'pdf', label: 'Documento PDF', icon: '📄' },
    { value: 'lab_report', label: 'Referto Laboratorio', icon: '🧪' },
    { value: 'rx', label: 'Radiografia', icon: '🩻' },
    { value: 'ultrasound', label: 'Ecografia', icon: '📱' },
    { value: 'ct', label: 'TAC', icon: '🏥' },
    { value: 'mri', label: 'Risonanza', icon: '🧲' },
    { value: 'ecg', label: 'Elettrocardiogramma', icon: '💓' },
    { value: 'video', label: 'Video', icon: '🎥' },
    { value: 'other', label: 'Altro', icon: '📁' }
  ];

  return (
    <div className="space-y-6 pb-8">
      {/* Document Management */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-indigo-500" />
              Archivio Documenti
            </div>
            <Button onClick={addDocument} size="sm">
              <Plus className="w-4 h-4 mr-2" />
              Aggiungi Documento
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.files && data.files.length > 0 ? (
            <div className="space-y-4">
              {data.files.map((file: any, index: number) => {
                const docType = documentTypes.find(type => type.value === file.type);
                const isEditing = editingDocument === index;
                const expiring = isExpiringSoon(file.expiry_date, file.reminder_days);
                const expired = isExpired(file.expiry_date);
                
                return (
                  <div key={index} className="border rounded-lg p-4">
                    {isEditing ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label>Tipo Documento *</Label>
                            <Select 
                              value={file.type || ''} 
                              onValueChange={(value) => updateDocument(index, 'type', value)}
                            >
                              <SelectTrigger className="bg-background/60 backdrop-blur-sm border-white/20">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-background/95 backdrop-blur-sm border-white/20 z-50">
                                {documentTypes.map((type) => (
                                  <SelectItem key={type.value} value={type.value}>
                                    <div className="flex items-center gap-2">
                                      <span>{type.icon}</span>
                                      <span>{type.label}</span>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Titolo *</Label>
                            <Input
                              value={file.title || ''}
                              onChange={(e) => updateDocument(index, 'title', e.target.value)}
                              placeholder="Nome del documento"
                            />
                          </div>
                          <div>
                            <Label>Data Rilascio</Label>
                            <Input
                              type="date"
                              value={file.issue_date || ''}
                              onChange={(e) => updateDocument(index, 'issue_date', e.target.value)}
                            />
                          </div>
                          <div>
                            <Label>Data Scadenza</Label>
                            <Input
                              type="date"
                              value={file.expiry_date || ''}
                              onChange={(e) => updateDocument(index, 'expiry_date', e.target.value)}
                            />
                          </div>
                          <div>
                            <Label>Ente Rilasciatore</Label>
                            <Input
                              value={file.issuing_authority || ''}
                              onChange={(e) => updateDocument(index, 'issuing_authority', e.target.value)}
                              placeholder="Chi ha rilasciato il documento"
                            />
                          </div>
                          <div>
                            <Label>Numero Documento</Label>
                            <Input
                              value={file.document_number || ''}
                              onChange={(e) => updateDocument(index, 'document_number', e.target.value)}
                              placeholder="Numero identificativo"
                            />
                          </div>
                          <div>
                            <Label>Giorni Promemoria Scadenza</Label>
                            <Input
                              type="number"
                              value={file.reminder_days || 30}
                              onChange={(e) => updateDocument(index, 'reminder_days', parseInt(e.target.value) || 30)}
                              placeholder="30"
                            />
                          </div>
                          <div>
                            <Label>Sezione Riferimento</Label>
                            <Select 
                              value={file.section_ref || ''} 
                              onValueChange={(value) => updateDocument(index, 'section_ref', value)}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Collega a sezione" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="health.vaccinations">Vaccinazioni</SelectItem>
                                <SelectItem value="health.checkups">Controlli Medici</SelectItem>
                                <SelectItem value="health.lab_tests">Esami Lab</SelectItem>
                                <SelectItem value="health.imaging">Diagnostica</SelectItem>
                                <SelectItem value="health.surgeries">Interventi</SelectItem>
                                <SelectItem value="identity.microchip">Microchip</SelectItem>
                                <SelectItem value="identity.registration">Registrazione</SelectItem>
                                <SelectItem value="competitions.events">Competizioni</SelectItem>
                                <SelectItem value="breeding.litters">Cucciolate</SelectItem>
                                <SelectItem value="insurance">Assicurazione</SelectItem>
                                <SelectItem value="general">Generale</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="md:col-span-2">
                            <Label>Descrizione</Label>
                            <Textarea
                              value={file.description || ''}
                              onChange={(e) => updateDocument(index, 'description', e.target.value)}
                              placeholder="Descrizione del documento..."
                              rows={2}
                            />
                          </div>
                          <div className="md:col-span-2">
                            <Label>URL File</Label>
                            <div className="flex gap-2">
                              <Input
                                value={file.file_url || ''}
                                onChange={(e) => updateDocument(index, 'file_url', e.target.value)}
                                placeholder="URL del file caricato"
                                className="flex-1"
                              />
                              <Button size="sm" variant="outline" className="shrink-0">
                                <Upload className="w-4 h-4 mr-2" />
                                Carica
                              </Button>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button onClick={() => saveDocument(index)} size="sm">
                            <Check className="w-4 h-4 mr-2" />
                            Salva
                          </Button>
                          <Button variant="outline" onClick={() => removeDocument(index)} size="sm">
                            <X className="w-4 h-4 mr-2" />
                            Rimuovi
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xl">{docType?.icon || '📁'}</span>
                            <h4 className="font-medium">{file.title || 'Documento'}</h4>
                            {file.expiry_date && (
                              <Badge variant={expired ? 'destructive' : expiring ? 'default' : 'secondary'}>
                                {expired && <AlertTriangle className="w-3 h-3 mr-1" />}
                                <Calendar className="w-3 h-3 mr-1" />
                                Scade: {new Date(file.expiry_date).toLocaleDateString('it-IT')}
                              </Badge>
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground space-y-1">
                            <p>Tipo: {docType?.label || 'Non specificato'}</p>
                            {file.document_number && <p>Numero: {file.document_number}</p>}
                            {file.issuing_authority && <p>Rilasciato da: {file.issuing_authority}</p>}
                            {file.issue_date && (
                              <p>Data rilascio: {new Date(file.issue_date).toLocaleDateString('it-IT')}</p>
                            )}
                            {file.description && <p>{file.description}</p>}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="ghost" size="sm" onClick={() => setEditingDocument(index)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => removeDocument(index)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">
              Nessun documento archiviato. Clicca "Aggiungi Documento" per iniziare.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Photo Gallery */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Image className="w-5 h-5 text-purple-500" />
            Galleria Fotografica
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="main_photo">Foto Principale</Label>
            <div className="flex gap-2 mt-1">
              <Input
                id="main_photo"
                value={data.photo_main || ''}
                onChange={(e) => handleChange('photo_main', e.target.value)}
                placeholder="URL della foto principale"
                className="flex-1"
              />
              <Button size="sm" variant="outline" className="shrink-0">
                <Camera className="w-4 h-4 mr-2" />
                Seleziona
              </Button>
            </div>
          </div>
          
          <div>
            <Label htmlFor="gallery_urls">URLs Galleria</Label>
            <Textarea
              id="gallery_urls"
              value={data.gallery?.join('\n') || ''}
              onChange={(e) => handleChange('gallery', e.target.value.split('\n').filter(Boolean))}
              placeholder="Un URL per riga"
              className="mt-1"
              rows={4}
            />
            <p className="text-sm text-muted-foreground mt-1">
              Inserisci un URL per riga per creare la galleria fotografica
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Document Notes */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-gray-500" />
            Note Generali
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div>
            <Label htmlFor="document_notes">Note sui Documenti</Label>
            <Textarea
              id="document_notes"
              value={data.notes || ''}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Note generali sui documenti, ubicazione fisica, promemoria..."
              className="mt-1"
              rows={4}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};